import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo } from "react";
import { AuthContextProvider, } from "./auth-context.js";
import { getSessionToken, isTokenExpired, refreshSession, logoutSession, fetchUserInfo, SESSION_EXPIRED_EVENT, } from "./token-manager.js";
const REFRESH_INTERVAL_MS = 60_000;
/**
 * Lightweight auth provider that reads Descope session tokens directly
 * from localStorage. Does NOT load the Descope SDK or web components.
 *
 * Fetches user info (email, name, picture) from Descope's /v1/auth/me
 * endpoint via a single HTTP call, replacing the SDK's useUser() hook.
 *
 * The full Descope SDK is only mounted where a Descope widget needs it: the
 * login routes, and the dashboard's settings page for the profile widget.
 */
export function SessionAuthProvider({ projectId, children, }) {
    const [isSessionLoading, setIsSessionLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(undefined);
    const [isUserLoading, setIsUserLoading] = useState(true);
    // Check session and fetch user info atomically.
    // isUserLoading stays true until both session check AND user info fetch
    // complete, preventing useProvisioning from firing with no email.
    const checkSession = useCallback(async () => {
        const token = getSessionToken();
        let authenticated;
        if (token && !isTokenExpired(token)) {
            authenticated = true;
        }
        else {
            const result = await refreshSession(projectId);
            authenticated = result.ok;
        }
        setIsAuthenticated(authenticated);
        setIsSessionLoading(false);
        if (authenticated) {
            const info = await fetchUserInfo(projectId);
            if (info) {
                setUser({
                    userId: info.userId,
                    email: info.email,
                    name: info.name,
                    picture: info.picture,
                });
            }
        }
        else {
            setUser(undefined);
        }
        setIsUserLoading(false);
    }, [projectId]);
    // Check session on mount
    useEffect(() => {
        checkSession();
    }, [checkSession]);
    // Periodic refresh to keep session alive
    useEffect(() => {
        if (!isAuthenticated)
            return;
        const interval = setInterval(async () => {
            const token = getSessionToken();
            if (!token || isTokenExpired(token)) {
                const result = await refreshSession(projectId);
                if (!result.ok) {
                    setIsAuthenticated(false);
                    setUser(undefined);
                }
            }
        }, REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [isAuthenticated, projectId]);
    // Listen for token changes from other tabs (storage event)
    useEffect(() => {
        function handleStorageChange(e) {
            if (e.key === "DS") {
                const token = e.newValue;
                if (token && !isTokenExpired(token)) {
                    setIsAuthenticated(true);
                    setIsSessionLoading(false);
                    // Re-run full check to also fetch user info
                    checkSession();
                }
                else {
                    setIsAuthenticated(false);
                    setUser(undefined);
                    setIsSessionLoading(false);
                    setIsUserLoading(false);
                }
            }
        }
        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, [checkSession]);
    // Listen for an unrecoverable session: the token layer dispatches this when
    // a refresh is rejected or an API call stays 401 after a retry. Flipping
    // isAuthenticated here is what gives every route an escape hatch, because
    // the app's auth gate then redirects to login carrying the current path.
    useEffect(() => {
        function handleSessionExpired() {
            setIsAuthenticated(false);
            setUser(undefined);
            setIsSessionLoading(false);
            setIsUserLoading(false);
        }
        window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    }, []);
    // Listen for same-tab session changes (from login/signup Descope flows).
    // The storage event only fires in other tabs, so login/signup pages
    // dispatch this custom event after the Descope flow writes tokens.
    useEffect(() => {
        function handleSessionChange() {
            setIsUserLoading(true);
            checkSession();
        }
        window.addEventListener("headerhawk:session-change", handleSessionChange);
        return () => window.removeEventListener("headerhawk:session-change", handleSessionChange);
    }, [checkSession]);
    const logout = useCallback(async () => {
        await logoutSession(projectId);
        setIsAuthenticated(false);
        setUser(undefined);
    }, [projectId]);
    const value = useMemo(() => ({
        session: { isAuthenticated, isSessionLoading },
        userState: { user, isUserLoading },
        actions: { logout },
    }), [isAuthenticated, isSessionLoading, user, isUserLoading, logout]);
    return _jsx(AuthContextProvider, { value: value, children: children });
}
//# sourceMappingURL=session-auth-provider.js.map